import clsx from "clsx";
import { ChevronRight, Search, Shield } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { getHelpSections } from "@/components/help/helpSections";
import { featureFlags } from "@/config/featureFlags";
import { useAuth } from "@/context/AuthContext";

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function Help() {
  const { profile } = useAuth();
  const [activeSection, setActiveSection] = useState("getting-started");
  const [searchQuery, setSearchQuery] = useState("");
  const [tocCollapsed, setTocCollapsed] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const isPlatformAdmin = profile?.user?.isPlatformAdmin ?? false;

  // Build sections with current feature flags so flagged content is excluded
  const sections = useMemo(() => getHelpSections(featureFlags), []);

  const filteredSections = sections.filter(
    (section) => (!section.adminOnly || isPlatformAdmin) && (!section.featureFlag || featureFlags[section.featureFlag]),
  );

  const searchedSections = searchQuery
    ? filteredSections.filter((s) => s.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : filteredSections;

  const currentSection = filteredSections.find((s) => s.id === activeSection);

  // Scroll content to top when section changes
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeSection]);

  return (
    <div className="flex h-full">
      {/* Table of Contents - Sidebar */}
      <aside
        className={clsx(
          "border-r border-border-primary bg-surface-primary/50 flex flex-col shrink-0 transition-all duration-300",
          tocCollapsed ? "w-0 overflow-hidden" : "w-72",
        )}
      >
        <div className="p-4 border-b border-border-primary">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-lg font-bold text-content-primary">Anthm Help</h2>
          </div>
          <p className="text-xs text-content-quaternary mb-4">Everything you need to know</p>
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
            <input
              type="text"
              placeholder="Search topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary placeholder-content-quaternary focus:outline-hidden focus:border-brand-primary/50 transition"
            />
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {/* General sections */}
          {searchedSections
            .filter((s) => !s.adminOnly)
            .map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={clsx(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all text-left",
                    isActive
                      ? "bg-linear-to-r from-brand-primary/20 to-brand-secondary/20 text-brand-primary border border-brand-primary/30"
                      : "text-content-tertiary hover:bg-interactive-hover hover:text-content-primary",
                  )}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="font-medium truncate">{section.title}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto shrink-0" />}
                </button>
              );
            })}

          {/* Admin sections */}
          {isPlatformAdmin && searchedSections.some((s) => s.adminOnly) && (
            <>
              <div className="pt-4 pb-2 px-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5 text-brand-secondary" />
                  <span className="text-xs font-semibold text-content-quaternary uppercase tracking-wider">
                    Administration
                  </span>
                </div>
              </div>
              {searchedSections
                .filter((s) => s.adminOnly)
                .map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={clsx(
                        "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all text-left",
                        isActive
                          ? "bg-linear-to-r from-brand-secondary/20 to-brand-secondary/10 text-brand-secondary border border-brand-secondary/30"
                          : "text-content-tertiary hover:bg-interactive-hover hover:text-content-primary",
                      )}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="font-medium truncate">{section.title.replace("Admin: ", "")}</span>
                      {isActive && <ChevronRight className="w-4 h-4 ml-auto shrink-0" />}
                    </button>
                  );
                })}
            </>
          )}
        </nav>
      </aside>

      {/* Toggle TOC button */}
      <button
        onClick={() => setTocCollapsed((prev) => !prev)}
        className="hidden lg:flex items-center justify-center w-6 bg-surface-primary/50 hover:bg-interactive-hover border-r border-border-primary text-content-quaternary hover:text-content-primary transition"
        title={tocCollapsed ? "Show table of contents" : "Hide table of contents"}
      >
        <ChevronRight className={clsx("w-4 h-4 transition-transform", !tocCollapsed && "rotate-180")} />
      </button>

      {/* Main Content */}
      <div ref={contentRef} className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6 lg:p-8">
          {currentSection ? (
            <>
              {/* Section Header */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  {currentSection.adminOnly && (
                    <span className="px-2 py-0.5 bg-brand-secondary-subtle text-brand-secondary text-xs font-medium rounded-full border border-brand-secondary/30">
                      Admin
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div
                    className={clsx(
                      "w-10 h-10 rounded-lg flex items-center justify-center",
                      currentSection.adminOnly
                        ? "bg-brand-secondary-subtle"
                        : "bg-linear-to-br from-brand-primary/20 to-brand-secondary/20",
                    )}
                  >
                    <currentSection.icon
                      className={clsx(
                        "w-5 h-5",
                        currentSection.adminOnly ? "text-brand-secondary" : "text-brand-primary",
                      )}
                    />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-content-primary">{currentSection.title}</h1>
                    <p className="text-sm text-content-tertiary mt-0.5">{currentSection.subtitle}</p>
                  </div>
                </div>
              </div>

              {/* Section Content */}
              {currentSection.content}

              {/* Navigation between sections */}
              <div className="mt-12 pt-6 border-t border-border-primary">
                {(() => {
                  const currentIndex = filteredSections.findIndex((s) => s.id === activeSection);
                  const prevSection = filteredSections[currentIndex - 1];
                  const nextSection = filteredSections[currentIndex + 1];
                  return (
                    <div className="flex items-stretch justify-between gap-4">
                      {prevSection ? (
                        <button
                          onClick={() => setActiveSection(prevSection.id)}
                          className="flex-1 group flex items-center gap-3 p-4 rounded-xl border border-border-primary hover:border-border-secondary hover:bg-interactive-hover transition text-left"
                        >
                          <ChevronRight className="w-5 h-5 rotate-180 text-content-quaternary group-hover:text-brand-primary transition shrink-0" />
                          <div className="min-w-0">
                            <p className="text-xs text-content-quaternary mb-0.5">Previous</p>
                            <p className="text-sm font-medium text-content-secondary group-hover:text-content-primary transition truncate">
                              {prevSection.title}
                            </p>
                          </div>
                        </button>
                      ) : (
                        <div className="flex-1" />
                      )}
                      {nextSection ? (
                        <button
                          onClick={() => setActiveSection(nextSection.id)}
                          className="flex-1 group flex items-center justify-end gap-3 p-4 rounded-xl border border-border-primary hover:border-border-secondary hover:bg-interactive-hover transition text-right"
                        >
                          <div className="min-w-0">
                            <p className="text-xs text-content-quaternary mb-0.5">Next</p>
                            <p className="text-sm font-medium text-content-secondary group-hover:text-content-primary transition truncate">
                              {nextSection.title}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-content-quaternary group-hover:text-brand-primary transition shrink-0" />
                        </button>
                      ) : (
                        <div className="flex-1" />
                      )}
                    </div>
                  );
                })()}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-content-quaternary">
              <p>Select a topic from the table of contents to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
